














import { computed, defineComponent, ref, watch } from "@vue/composition-api";
import { intervalToDuration } from "date-fns";
import useAuthUser from "@/hooks/useAuthUser";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import providers from "@/lib/api";
import config from "@/config";
import AgeVerification from "@/components/AgeVerification";
import SensitiveContentAllowed from "./components/SensitiveContentAllowed";
import SensitiveContentConfirm from "./components/SensitiveContentConfirm";
import SensitiveContentDeclined from "./components/SensitiveContentDeclined";
import SensitiveContentSetupAge from "./components/SensitiveContentSetupAge";

export default defineComponent({
  name: "ConfirmSensitiveContent",
  components: {
    AgeVerification,
    SensitiveContentDeclined,
    SensitiveContentAllowed,
    SensitiveContentSetupAge,
    SensitiveContentConfirm,
  },
  props: {
    forceShow: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { authUser } = useAuthUser();
    const { updateNSFWUserSettings: update, nSFWUserSettings, showNotSafeForWork } = useNSFWUserSettings();
    const showConfirm = ref(false);
    const showSetupAge = ref(false);
    const dateOfBirth = ref<Date>();
    const showAllowed = ref(false);
    const showDeclined = ref(false);

    const checkNeedShowConfirm = () => {
      showConfirm.value = !config.featureNSFW ? false : props.forceShow ? true : nSFWUserSettings.value === null;
    };

    const nsfwVerificationType = computed(() =>
      config.featureNSFW ? (config.featureYotiAgeVerification ? "yoti" : "default") : ""
    );

    let unWatch = () => {};

    const watchOnSetting = (immediate = false) => {
      unWatch = watch(
        nSFWUserSettings,
        () => {
          checkNeedShowConfirm();
          if (!showConfirm.value) emit("resolve", showNotSafeForWork.value);
        },
        { immediate }
      );
    };

    const updateNSFWUserSettings = async (value: boolean) => {
      unWatch();
      await update(value);
      watchOnSetting();
    };

    const getPersonalInfo = async () => {
      if (!authUser.value) return;
      const response = await providers.PersonalInformationProvider.find({
        id: authUser.value.id,
      });
      response.dateOfBirth && (dateOfBirth.value = new Date(response.dateOfBirth));
    };

    const updatePersonalInfo = async () => {
      if (!authUser.value) return;
      await providers.PersonalInformationProvider.update({
        data: {
          userId: authUser.value.id,
          dateOfBirth: dateOfBirth.value?.toISOString() ?? "",
        },
      });
    };

    const close = () => {
      emit("resolve", showNotSafeForWork.value);
      showConfirm.value = showAllowed.value = showDeclined.value = false;
    };

    const confirm = async (value: boolean, cb?: () => void) => {
      try {
        if (value) {
          await getPersonalInfo();
          showConfirm.value = false;
          showSetupAge.value = true;
        } else {
          await updateNSFWUserSettings(value);
          close();
        }
      } finally {
        cb && cb();
      }
    };

    const submitAge = async (value: string | null, cb?: () => void) => {
      try {
        let result = false;
        if (value) {
          dateOfBirth.value = new Date(value);
          const { years = 0 } = intervalToDuration({ start: dateOfBirth.value, end: new Date(Date.now()) });
          await updatePersonalInfo();
          result = years > 18;
        }
        await updateNSFWUserSettings(result);
        result ? (showAllowed.value = true) : (showDeclined.value = true);
        showSetupAge.value = false;
      } finally {
        cb && cb();
      }
    };

    watchOnSetting(true);

    return {
      showConfirm,
      showSetupAge,
      showAllowed,
      showDeclined,
      dateOfBirth,
      nsfwVerificationType,
      confirm,
      submitAge,
      close,
    };
  },
});
